'use client';

import React, { useState, useEffect } from 'react';
import UnavailableDeliveryMethod from '@app/pdp/components/AddToCart/DeliveryMethods/UnavailableDeliveryMethod';
import type {
    BopisProps,
    ProductData,
} from '@app/pdp/components/AddToCart/DeliveryMethods/types';

import { getAvailableQuantities } from '@app/pdp/components/AddToCart/DeliveryMethods/utils';
import AddToCartBtn from '@app/pdp/components/AddToCart/common/AddToCartBtn';
import MinimumQuantityLabel from '@app/pdp/components/AddToCart/common/MinimumQuantityLabel';
import { useAddToCart } from '@app/pdp/components/AddToCart/hooks/useAddToCart';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { EstimatedTreatPoints } from '@app/pdp/components/EstimatedTreatPoints';
import { DELIVERY_MESSAGE, DELIVERY_METHODS } from '@app/pdp/constants';
import { useProductDetails } from '@app/pdp/hooks';
import { VetRxCats } from '@app/pdp/types';
import {
    ExperimentVersion,
    STORE_SERVICES,
    clsx,
    useExperimentValue,
    usePageLayout,
} from '@libs/common-next';
import { DeliveryType, getCountry, stringifyStoreId } from '@libs/core';
import { AddToCartType, getInventoryStoreId } from '@libs/ocapi';
import { hasStockType, StoreTooltip } from '@libs/shared-ui';
import type { StockStatusType } from '@libs/shared-ui';
import {
    Button,
    RadioField,
    SelectField,
    StockStatusEnum,
    StockStatusLabel,
    TextPassage,
} from '@petsmart-ui/sparky';
import CurbsideDrawer from './CurbsideDrawer';

import './Bopis.scss';

export default function Bopis({
    product,
    value,
    curbsideInfo,
    stockStatus,
    isActive,
    isDisabled,
    isDisabledByPersonalization,
    handleAddToCart,
    vetRxCats,
    minimumCaseQty,
    inventoryQuantity,
    isPageLoading,
}: BopisProps) {
    const {
        errorAlert,
        selectedStore: { store },
        fullStoreListResults,
        setStoreDrawerOpen,
        setErrorAlert,
        setRefreshNeededError,
    } = usePageLayout();

    const country = getCountry();

    const { addToCTCart, isLoading: isCTLoading } = useAddToCTCart();
    const { setCanAddToCart, selectedProduct } = useProductDetails();
    const currentVariantId = Number(selectedProduct.id || product.id);
    const experimentValue = useExperimentValue();
    const { handleAddToCartAction: addToCart } = useAddToCart(handleAddToCart);

    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCTCartLoading, setIsCTCartLoading] = useState(false);
    const [validStockStatus, setValidStockStatus] =
        useState<StockStatusType>(stockStatus);

    const [showStockInOtherStores, setShowStockInOtherStores] =
        useState<boolean>(false);

    useEffect(() => {
        // isCTCartLoading state must be behind a feature flag until CT cart is released to production
        setIsCTCartLoading(
            experimentValue === ExperimentVersion.FULL_SOLUTION && isCTLoading
        );
    }, [experimentValue, isCTCartLoading, isCTLoading]);

    useEffect(() => {
        if (errorAlert && isLoading) {
            setIsLoading(false);
        }
    }, [errorAlert, isLoading, setIsLoading]);

    // defaulting to true when there is no store. This keeps the UI consistent with previous implementation.
    const bopisEnabled = store ? store.isBopisEnabled : true;
    const isAvailable = store && hasStockType(validStockStatus) && bopisEnabled;

    useEffect(() => {
        setCanAddToCart(Boolean(isAvailable));
    }, [isAvailable, setCanAddToCart]);

    const hasStoresWithStock = fullStoreListResults.find(
        ({ Store }) => Store.HasCurrentProductInStock
    );

    useEffect(() => {
        if (!bopisEnabled) {
            setValidStockStatus(StockStatusEnum.OutOfStock);
        } else {
            setValidStockStatus(stockStatus);
        }
    }, [bopisEnabled, stockStatus]);

    useEffect(() => {
        setShowStockInOtherStores(
            (!isAvailable && hasStoresWithStock !== undefined) || !bopisEnabled
        );
    }, [bopisEnabled, hasStoresWithStock, isAvailable]);

    const [selectedQty, setSelectedQty] = useState(minimumCaseQty || 1);

    const hasCurbsidePickup = store?.StoreServices?.some(
        service => service.ServiceId === STORE_SERVICES.CURBSIDE_PICKUP
    );

    const stockStatusLabel = (
        <div className="bopis-stock-info">
            {stockStatus !== 'Pending' && !showStockInOtherStores ? (
                <div className="stock-status">
                    <StockStatusLabel
                        stockStatus={validStockStatus as StockStatusEnum}
                        labelInStock="In stock at"
                        labelFewLeftInStock="Only a few left at"
                        labelOutOfStock="Out of stock at"
                    />
                    <StoreTooltip />
                </div>
            ) : null}
        </div>
    );

    const onQuantityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const qty = event?.target.value;

        if (qty) {
            setSelectedQty(parseInt(qty, 10));
        }
    };

    const bopisTextMsg = (): string => {
        if (isAvailable) {
            if (vetRxCats?.includes(VetRxCats.RX)) {
                return `${DELIVERY_MESSAGE.estimated} ${DELIVERY_MESSAGE.vetRxSuffix}`;
            }
            if (vetRxCats?.includes(VetRxCats.VET)) {
                return `${DELIVERY_MESSAGE.ready} ${DELIVERY_MESSAGE.vetRxSuffix}`;
            }
            return DELIVERY_MESSAGE.ready;
        }

        if (!bopisEnabled) {
            return DELIVERY_MESSAGE.nearbyStores;
        }

        return hasStoresWithStock
            ? DELIVERY_MESSAGE.nearbyStores
            : DELIVERY_MESSAGE.ready;
    };

    const availableQuantities = getAvailableQuantities(
        minimumCaseQty,
        inventoryQuantity
    );

    if (isDisabledByPersonalization) {
        return <UnavailableDeliveryMethod deliveryMethod="Pick up in store" />;
    }

    const handleFormSubmit = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        if (!isAvailable || isPageLoading) {
            return;
        }

        if (!store) {
            console.error('No store selected.');
            return;
        }

        setIsLoading(true);

        const body: ProductData = [
            {
                product_id: currentVariantId.toString(),
                quantity: selectedQty,
                inventory_id: getInventoryStoreId(store.StoreNumber),
                c_addToCartType: AddToCartType.STANDARD,
                c_fromStoreId: stringifyStoreId(store.StoreNumber),
                c_isScheduleDelivLineItem: false,
            },
        ];

        try {
            const response = await addToCart(body);

            if (!response) {
                setIsLoading(false);
                console.error(
                    `BOPIS: no response from adding BOPIS item to cart. Request parameters: ${JSON.stringify(
                        body
                    )}`
                );
                return;
            }
            setIsLoading(false);
            setAddedToCart(true);
        } catch (e) {
            setRefreshNeededError(true);
            setErrorAlert(true);
            console.error(
                `BOPIS: Error while attempting add BOPIS item to cart. Request parameters: ${JSON.stringify(
                    body
                )}`,
                e
            );
            setIsLoading(false);
        }
    };

    const handleCommercetoolsFormSubmit = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        if (!store || isCTCartLoading || isPageLoading) {
            return;
        }

        const body = {
            country,
            sku: currentVariantId.toString(),
            quantity: selectedQty,
            fulfillmentMethod: DeliveryType.BOPIS,
            storeId: stringifyStoreId(store.StoreNumber),
        };

        await addToCTCart(body);
    };

    const openStoreDrawer = (e: React.MouseEvent) => {
        e.preventDefault();
        setStoreDrawerOpen({ pageType: 'pdp' });
    };

    const hiddenFormContent = () => (
        <form
            onSubmit={
                experimentValue === ExperimentVersion.FULL_SOLUTION
                    ? handleCommercetoolsFormSubmit
                    : handleFormSubmit
            }
            data-testid="form-bopis"
        >
            <div className="bopis-body">
                {hasCurbsidePickup && !showStockInOtherStores && (
                    <CurbsideDrawer curbsideInfo={curbsideInfo} />
                )}
                <div className="change-store-section">
                    {store && stockStatusLabel}

                    {(isAvailable || !hasStoresWithStock) && (
                        <Button
                            text={
                                store && store.StoreNumber
                                    ? 'Change store'
                                    : 'Find a store near me'
                            }
                            variant="link"
                            className="change-store-btn"
                            onClick={openStoreDrawer}
                        />
                    )}
                </div>
            </div>
            <MinimumQuantityLabel minimumCaseQty={minimumCaseQty} />
            {isAvailable && store && (
                <>
                    <SelectField
                        value={selectedQty}
                        onChange={onQuantityChange}
                        label="Quantity"
                        items={availableQuantities}
                        disabled={availableQuantities.length < 1}
                    />
                    <EstimatedTreatPoints
                        qty={selectedQty}
                        shopId={store.StoreNumber}
                    />
                    <br />
                    <AddToCartBtn
                        text={
                            addedToCart
                                ? DELIVERY_METHODS.ADDED_TO_CART
                                : DELIVERY_METHODS.ADD_TO_CART
                        }
                        className={clsx(
                            addedToCart &&
                                'add-to-cart-button add-to-cart-button__green'
                        )}
                        setAddedToCart={setAddedToCart}
                        addedToCart={addedToCart}
                        isLoading={
                            isLoading || isCTCartLoading || isPageLoading
                        }
                    />
                </>
            )}
            {showStockInOtherStores && (
                <Button
                    loading={isPageLoading}
                    className="alternative-btn"
                    text="Show in-stock stores"
                    variant="primary"
                    onClick={openStoreDrawer}
                />
            )}

            {!isAvailable && !hasStoresWithStock && bopisEnabled && (
                <AddToCartBtn disabled />
            )}
        </form>
    );

    return (
        <RadioField.Item
            className="bopis"
            disabled={isDisabled && bopisEnabled}
            value={value}
            text={
                <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
                    Pick up in store
                </span>
            }
            itemAfter={
                isDisabled && bopisEnabled ? (
                    <TextPassage className="delivery-method-status sparky-u-color-utility-warning">
                        Not available for this item
                    </TextPassage>
                ) : (
                    <>
                        <TextPassage>{bopisTextMsg()}</TextPassage>
                        {!isActive && store && stockStatusLabel}
                    </>
                )
            }
            hiddenContent={hiddenFormContent()}
        />
    );
}
